import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { BranchSummary } from '../../api/branches';

const branchFormSchema = z.object({
  name: z.string().trim().min(1, 'Required'),
  code: z.string().trim().min(1, 'Required').max(20),
  address: z.string().trim().optional().or(z.literal('')),
  city: z.string().trim().optional().or(z.literal('')),
  country: z.string().trim().optional().or(z.literal('')),
  phone: z.string().trim().optional().or(z.literal('')),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')),
});

export type BranchFormValues = z.infer<typeof branchFormSchema>;

interface BranchFormDialogProps {
  open: boolean;
  onClose: () => void;
  editingBranch: BranchSummary | null;
  errorMessage: string | null;
  onSubmit: (values: BranchFormValues) => Promise<void>;
}

export const BranchFormDialog = ({
  open,
  onClose,
  editingBranch,
  errorMessage,
  onSubmit,
}: BranchFormDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BranchFormValues>({ resolver: zodResolver(branchFormSchema) });

  useEffect(() => {
    if (open) {
      reset(
        editingBranch
          ? {
              name: editingBranch.name,
              code: editingBranch.code,
              address: editingBranch.address ?? '',
              city: editingBranch.city ?? '',
              country: editingBranch.country ?? '',
              phone: editingBranch.phone ?? '',
              email: editingBranch.email ?? '',
            }
          : { name: '', code: '', address: '', city: '', country: '', phone: '', email: '' },
      );
    }
  }, [open, editingBranch, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingBranch ? 'Edit branch' : 'New branch'}</DialogTitle>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
              <TextField
                label="Code"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
                {...register('code')}
              />
            </Stack>
            <TextField label="Address" fullWidth {...register('address')} />
            <Stack direction="row" spacing={2}>
              <TextField label="City" fullWidth {...register('city')} />
              <TextField label="Country" fullWidth {...register('country')} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Phone" fullWidth {...register('phone')} />
              <TextField
                label="Email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register('email')}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : editingBranch ? 'Save changes' : 'Create branch'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
